import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { SettingsShell } from "@/components/SettingsShell";
import { Skeleton } from "@/components/Skeleton";

/**
 * /settings/notifications — toggles map 1-to-1 to notification_prefs
 * columns and genuinely shape the inbox (see inboxFilter in
 * src/server/routes/me.ts). Only toggles with real behavior render —
 * mentions / due reminders / the weekly digest have no engine yet, and a
 * switch that does nothing reads as broken (the columns stay in the DB
 * for when those features land).
 */

const ROWS = [
  {
    key: "taskAssigned" as const,
    label: "Task assigned to me",
    caption: "Inbox events when someone assigns a task to you.",
  },
  {
    key: "comments" as const,
    label: "Comments on my tasks",
    caption: "Inbox events for new comments on tasks you're involved in.",
  },
  {
    key: "projectUpdates" as const,
    label: "Project updates",
    caption:
      "Inbox events when a project you're a member of is created or edited.",
  },
];

type NotifKey = (typeof ROWS)[number]["key"];
type NotifPrefs = Record<NotifKey, boolean>;

function NotificationsSettings() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: queryKeys.notificationPrefs(),
    queryFn: api.notificationPrefs,
  });
  const m = useMutation({
    mutationFn: (input: Partial<NotifPrefs>) =>
      api.updateNotificationPrefs(input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.notificationPrefs() }),
  });

  return (
    <SettingsShell>
      <div className="panel">
        <div className="panel-head">
          <h3>Notifications</h3>
          <p>Choose what you're notified about and where.</p>
        </div>
        <div className="panel-body">
          {q.isLoading ? (
            <Skeleton count={3} height={56} />
          ) : (
            ROWS.map((r, i) => {
              const value = q.data?.[r.key] ?? false;
              return (
                <div
                  key={r.key}
                  className="opt"
                  style={i === 0 ? { paddingTop: 0 } : undefined}
                >
                  <div className="ot">
                    <div className="otit">{r.label}</div>
                    <div className="odesc">{r.caption}</div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={value}
                    aria-label={r.label}
                    className={`switch${value ? " on" : ""}`}
                    disabled={m.isPending}
                    onClick={() => m.mutate({ [r.key]: !value })}
                  />
                </div>
              );
            })
          )}
          <p className="t-sm t-muted" style={{ margin: "14px 0 0" }}>
            @mention alerts, due-date reminders, and the weekly digest email are
            on the roadmap and will appear here when they ship.
          </p>
        </div>
      </div>
    </SettingsShell>
  );
}

export const Route = createFileRoute("/settings/notifications")({
  component: NotificationsSettings,
});
